import { useTheme } from '@lifeomic/chroma-react/styles';
import { lighten } from '@mui/material/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createContainer,
  Flyout,
  FlyoutProps,
  VictoryAxis,
  VictoryChart,
  VictoryClipContainer,
  VictoryLabel,
  VictoryLine,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip,
} from 'victory';
import { ResultHistory } from '../../../../../../../services/MedicalResultsService';
import { GRAPH_HEIGHT, LABEL_X_POSITION } from './constants';
import { intoChartData, ScatterChartData } from './utils';

const VictoryVoronoiZoomContainer = createContainer('voronoi', 'zoom') as any;

type GraphProps = {
  medicalResults: ResultHistory[];
  containerWidth: number;
};

export function Graph({ medicalResults, containerWidth }: GraphProps) {
  const theme = useTheme();
  const valueLabelRef = useRef<HTMLDivElement>(null);
  const [valueLabelWidth, setValueLabelWidth] = useState(0);

  const {
    scatterChartData,
    metadata: { minYValue, maxYValue },
  } = useMemo(() => intoChartData(medicalResults), [medicalResults]);

  useEffect(() => {
    if (valueLabelRef.current) {
      setValueLabelWidth(valueLabelRef.current.offsetWidth);
    }
  }, [scatterChartData]);

  const minDomain = minYValue ?? { value: 0 };
  const maxDomain = maxYValue ?? { value: 0 };
  const zoomDomain = {
    x: [
      Math.max(0, scatterChartData.length - 10),
      scatterChartData.length - 1,
    ] as [number, number],
  };
  const tickValues = Array.from(
    { length: scatterChartData.length },
    (_, i) => i,
  );

  const getYTickValues = (direction: 'left' | 'right') => {
    if (
      direction === 'left' &&
      !minDomain.valueDisplay &&
      !maxDomain.valueDisplay
    ) {
      return [
        minDomain.value,
        Number(((minDomain.value + maxDomain.value) / 2).toPrecision(4)),
        maxDomain.value,
      ];
    }
    return [minDomain.value, maxDomain.value];
  };

  const formatYTickValue = (tick: number) => {
    const datum = scatterChartData.find(d => d.y === tick);
    return datum?.valueDisplay ?? datum?.y ?? tick;
  };

  const biomarkerName = medicalResults[0]?.coding?.display ?? '';

  return (
    <>
      <div
        ref={valueLabelRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          fontFamily: theme.typography.fontFamily,
          fontSize: 28,
          letterSpacing: -0.75,
        }}
      >
        {scatterChartData[scatterChartData.length - 1]?.valueDisplay ??
          scatterChartData[scatterChartData.length - 1]?.y}
      </div>
      <VictoryChart
        theme={{
          axis: {
            ...VictoryTheme.material.axis,
            style: {
              ...VictoryTheme.material.axis?.style,
              axis: { stroke: 'transparent' },
              grid: { stroke: 'transparent' },
              ticks: { stroke: 'transparent' },
              tickLabels: { fill: 'transparent' },
            },
          },
        }}
        padding={{ top: 34, right: 130, bottom: 32, left: 45 }}
        containerComponent={
          <VictoryVoronoiZoomContainer
            style={{
              cursor: scatterChartData.length > 10 ? 'grab' : 'default',
            }}
            voronoiDimension="x"
            clipContainerComponent={
              <VictoryClipContainer
                clipPadding={{ top: 12, right: 12, left: 6, bottom: 12 }}
                translateX={38}
              />
            }
            zoomDomain={zoomDomain}
            allowZoom={false}
            allowPan
          />
        }
        minDomain={{ y: minDomain.value }}
        maxDomain={{ y: maxDomain.value }}
        height={GRAPH_HEIGHT}
        width={containerWidth}
      >
        <VictoryLabel
          x={0}
          y={9}
          // limit the length of the name
          text={
            biomarkerName.length > 80
              ? biomarkerName.slice(0, 80) + '…'
              : biomarkerName
          }
          style={{
            fill: '#262c32',
            fontFamily: theme.typography.fontFamily,
            fontSize: 16,
            fontWeight: 'bold',
          }}
        />
        {scatterChartData.length > 0 && (
          <>
            <VictoryLabel
              x={LABEL_X_POSITION(containerWidth)}
              y={16}
              datum={scatterChartData[scatterChartData.length - 1]}
              text={({ datum }) => datum.valueDisplay ?? datum.y}
              style={{
                fill: (fillProps?: { datum?: ScatterChartData }) =>
                  fillProps?.datum?.color ?? '#576674',
                fontFamily: theme.typography.fontFamily,
                fontSize: 28,
                letterSpacing: -0.75,
              }}
            />
            <VictoryLabel
              x={LABEL_X_POSITION(containerWidth) + valueLabelWidth + 4}
              y={16}
              datum={scatterChartData[scatterChartData.length - 1]}
              text={({ datum }) => (datum.unit ? ` ${datum.unit}` : '')}
              style={{
                fill: lighten('#576674', 0.3),
                fontFamily: theme.typography.fontFamily,
                fontSize: 10,
              }}
            />
            <VictoryLabel
              x={LABEL_X_POSITION(containerWidth)}
              y={39}
              datum={scatterChartData[scatterChartData.length - 1]}
              text={({ datum }) => {
                if (!datum.delta) return '';
                return datum.delta > 0
                  ? `↑ ${datum.delta}`
                  : `↓ ${Math.abs(datum.delta)}`;
              }}
              style={{
                fill: (fillProps?: { datum?: ScatterChartData }) => {
                  if (!fillProps?.datum?.delta) return '#576674';
                  return fillProps.datum.delta > 0 ? '#387f15' : '#D2691E';
                },
                fontFamily: theme.typography.fontFamily,
                fontSize: 14,
              }}
            />
          </>
        )}
        <VictoryAxis
          style={{
            axis: { stroke: lighten('#dbdbdb', 0.35) },
          }}
          tickLabelComponent={
            <VictoryLabel
              style={[
                {
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 10,
                  fill: '#576674',
                  textTransform: 'uppercase',
                },
                {
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 10,
                  fill: '#576674',
                  textTransform: 'uppercase',
                },
              ]}
              textAnchor="start"
              transform="translate(-10, 8)"
              lineHeight={1.2}
            />
          }
          tickValues={tickValues}
          tickFormat={(tick: number) =>
            intoTickFormat(scatterChartData, tick) as any
          }
        />
        {(['right', 'left'] as const).map(direction => (
          <VictoryAxis
            style={{
              grid: {
                stroke:
                  direction === 'left'
                    ? lighten('#dbdbdb', 0.35)
                    : 'transparent',
                strokeWidth: 1,
              },
            }}
            key={`victory-axis-${direction}`}
            tickLabelComponent={
              <VictoryLabel
                dx={direction === 'left' ? -10 : 10}
                style={[
                  {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 12,
                    fill: '#576674',
                    display: direction === 'left' ? 'block' : 'none',
                  },
                  {
                    fontFamily: theme.typography.fontFamily,
                    fontSize: 12,
                    fill: '#576674',
                    display: direction === 'left' ? 'block' : 'none',
                  },
                ]}
              />
            }
            dependentAxis
            tickValues={getYTickValues(direction)}
            orientation={direction}
            tickFormat={formatYTickValue}
          />
        ))}
        <VictoryLine
          data={scatterChartData}
          style={{
            data: {
              stroke: '#dbdbdb',
              strokeWidth: 3,
            },
          }}
        />
        <VictoryScatter
          data={scatterChartData}
          labelComponent={
            <VictoryTooltip
              dx={10}
              style={[
                {
                  fill: (fillProps?: { datum?: ScatterChartData }) =>
                    fillProps?.datum?.color ?? '#576674',
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 20,
                  fontWeight: 'bold',
                },
                {
                  baselineShift: -4,
                  fill: lighten('#576674', 0.3),
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 10,
                },
                {
                  baselineShift: -12,
                  fill: (fillProps?: { datum?: ScatterChartData }) => {
                    if (!fillProps?.datum?.delta) return '#576674';
                    return fillProps.datum.delta > 0 ? '#387f15' : '#D2691E';
                  },
                  fontFamily: theme.typography.fontFamily,
                  fontSize: 14,
                },
              ]}
              cornerRadius={4}
              flyoutStyle={{
                fill: '#f5f8fa',
                stroke: 'transparent',
              }}
              flyoutPadding={{ top: 10, bottom: 10, left: 15, right: 15 }}
              textAnchor="start"
              orientation="right"
              flyoutComponent={<CustomFlyout />}
              constrainToVisibleArea
            />
          }
          symbol={({ datum }: { datum: ScatterChartData }) =>
            datum.extracted ? 'diamond' : datum.pending ? 'square' : 'circle'
          }
          labels={({ datum }: { datum: ScatterChartData }) => {
            const value = datum.valueDisplay ?? `${datum.y}`;
            const unit = datum.unit ? ` ${datum.unit}` : '';
            const deltaParts = datum.delta
              ? [
                  datum.delta > 0
                    ? `↑ ${datum.delta}`
                    : datum.delta < 0
                    ? `↓ ${Math.abs(datum.delta)}`
                    : '',
                ]
              : [];
            const pendingParts = datum.pending ? ['(Pending)'] : [];
            const extractedParts = datum.extracted ? ['(Extracted)'] : [];
            return [
              value,
              unit,
              ...deltaParts,
              ...pendingParts,
              ...extractedParts,
            ];
          }}
          size={({ active }) => (active ? 6 : 4)}
          style={{
            data: {
              fill: '#ffffff',
              stroke: ({ datum }: { datum?: ScatterChartData }) =>
                datum?.color ?? '#dbdbdb',
              strokeWidth: 3,
            },
          }}
        />
      </VictoryChart>
    </>
  );
}

function CustomFlyout(props: FlyoutProps) {
  // with VictoryVoronoiZoomContainer we occasionally get a flyout with an undefined x value
  // this prevents the app from crashing
  // tooltips remain unaffected
  if (props.x === undefined) {
    return null;
  }
  return <Flyout {...props} />;
}

function intoTickFormat(scatterChartData: ScatterChartData[], tick: number) {
  const currTick = scatterChartData[tick]?.dateTime;

  if (!currTick) {
    return ['', '?'];
  }
  const monthAndDate = currTick.toFormat('d MMM');

  const year = currTick.toFormat('yyyy');

  return [monthAndDate, year];
}
